import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as Calender } from '../../../../assets/icons/calender.svg';
import { ReactComponent as Refresh } from '../../../../assets/icons/refresh-fill-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  RequestStationActivityInterface,
  StationActivityInterface,
} from '../../../../interface/station-activity-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  disableStationActivityFiltersApplied,
  fetchStationActivity,
  fetchStationActivityFilters,
  updateStationActivityFilters,
  setSortData,
} from '../../../../redux/slices/StationActivitySlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import OcppLogSidePanel from '../../../../shared-components/table-side-panel/OcppLogsSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../../utils/api/station-management';

/**
 * Station activity component
 * @returns {JSX.Element} The rendered component
 */
function StationActivity() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    stationsActivity,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.stationActivity);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [lastMins, setLastMins] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const column = useMemo(
    () => [
      {
        label: t('date'),
        accessorKey: 'created_at' as keyof StationActivityInterface,
        sortAllow: true,

        /**
         * Converting timestamp to UTC date
         * @param {StationActivityInterface} stationActivity The activity object
         * @returns {any} The rendered element
         */
        customData: (stationActivity: StationActivityInterface) => (
          <p>{ConvertUTCtoDate(stationActivity.created_at)}</p>
        ),
      },
      {
        label: t('time'),
        accessorKey: 'time' as keyof StationActivityInterface,
        sortAllow: true,
        /**
         * Converting timestamp to UTC time
         * @param {StationActivityInterface} stationActivityTime The activity object
         * @returns {any} The rendered element
         */
        customData: (stationActivityTime: StationActivityInterface) => (
          <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.created_at))}</p>
        ),
      },
      {
        label: t('type'),
        accessorKey: 'type' as keyof StationActivityInterface,
        sortAllow: false,

        /**
         * Converting timestamp to UTC time
         * @param {StationActivityInterface} stationActivity The activity object
         * @returns {any} The rendered element
         */
        customData: (stationActivity: StationActivityInterface) => {
          const type = stationActivity.type?.toLowerCase();

          return (
            <p className={`${type === 'in' ? 'type-in' : 'type-out'}`}>
              {type ? t(type as 'in' | 'out') : '-'}
            </p>
          );
        },
      },
      {
        label: t('messageType'),
        accessorKey: 'action' as keyof StationActivityInterface,
      },
      {
        label: t('message'),
        accessorKey: 'message' as keyof StationActivityInterface,
        columnWidth: 500,
      },
    ],
    [t],
  );

  /**
   * Handle the fetch the data in api function
   * @param {RequestStationActivityInterface} data The fetch params
   */
  const fetchData = (data: RequestStationActivityInterface) => {
    if (!loading && selectedChargingPoint) {
      dispatch(
        fetchStationActivity({
          ...data,
          chargingStation: selectedChargingPoint?.id,
        }),
      );
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const ampm = hours >= 12 ? t('pm') : t('am');
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      setLastUpdated(`${formattedHours}:${formattedMinutes} ${ampm}`);
    }
  };
  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'created_at' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('type'), accessorKey: 'type' },
    { label: t('actionType'), accessorKey: 'action' },
    { label: t('message'), accessorKey: 'message' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function for pdf and csv
   * @param {string} type pdf or csv
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    stationManagementApi
      .fetchStationActivityExport({
        ...queryParameters,
        chargingStation: selectedChargingPoint?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: StationActivityInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'created_at':
                return ConvertUTCtoDate(row?.created_at) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row?.created_at) || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'Station Activity.csv');
        } else {
          exportPDF(headers, rows, 'Station Activity.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Table pagination function
   * @param {any} value The page props
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      last15min: lastMins,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {'asc' | 'desc'} sortData.sortBy The sort order
   * @param {string} sortData.sortField The field to sort by
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
      last15min: lastMins,
    });
  };

  /**
   * Function to add search params, side panel will open automatically
   * @param {string} stationId The station id
   */
  const handleOpenSidePanelOnRowClick = (stationId: string) => {
    // Set or update the logId param
    searchParameters.set('stationId', stationId);

    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit,
        last15min: lastMins,
      });

      dispatch(disableStationActivityFiltersApplied());
    }
  }, [filters, selectedChargingPoint]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('StationActivityFilters');

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchStationActivityFilters({ chargingStation: currentId }));
    }
    if (stationsActivity.length === 0 || lastId !== currentId) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        last15min: lastMins,
      });

      dispatch(disableStationActivityFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('StationActivityFilters', currentId);
  }, [selectedChargingPoint?.id, filters]);

  return (
    <div className="station-activity-wrap" dir={i18n.dir()}>
      <div className="station-activity-table">
        <CustomTable
          version="v2"
          header={column}
          data={stationsActivity}
          serialNo
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          filtersToDispatch={updateStationActivityFilters}
          filterListToDispatch={fetchStationActivityFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          ReferenceCurrentPages={handleRefresh}
          rightTopContent={
            <div className="top-right-content">
              <div className="last-15-mins-button">
                <MyButton
                  label={t('last15Mins')}
                  buttonType={lastMins ? 'primary-outline-none' : 'none'}
                  svgIcon={<Calender />}
                  onClickFunc={() => {
                    if (selectedChargingPoint) {
                      const newLastMins = !lastMins;
                      setLastMins(newLastMins);
                      const queryParameters = GetParametersFromCommonFilters(filters);
                      fetchData({
                        page: DefaultApiLimit.page,
                        limit,
                        ...queryParameters,
                        last15min: newLastMins,
                      });
                    }
                  }}
                />
              </div>
              <div className="auto-refreshed-time">
                <Refresh />
                <p>
                  {t('lastUpdated')} {lastUpdated}
                </p>
              </div>
            </div>
          }
          pageLimitChange={pageNateLimitChange}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="stationId"
          rowClickSidePanel={<OcppLogSidePanel panelType="station" fetchIdFrom="queryParams" />}
          height={window.innerHeight >= 700 ? 320 : 339}
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
    </div>
  );
}

export default StationActivity;
